<template>
  <div :class="['ds-icon', `ds-icon--${variant}`, `ds-icon--align-${align}`]">
    <span
      v-if="variant === 'bullet'"
      class="ds-icon__circle"
      :class="{ 'ds-icon__circle--image': iconSrc }"
    >
      <img
        v-if="iconSrc"
        :src="iconSrc"
        alt=""
        aria-hidden="true"
        class="ds-icon__image"
      >
      <i v-else :class="icon" :style="iconStyle" aria-hidden="true" />
    </span>
    <i v-else :class="icon" :style="iconStyle" aria-hidden="true" />
    <span class="ds-icon__text">
      <span :class="{ 'ds-icon__text--responsive': desktopText }">{{ text }}</span>
      <span v-if="desktopText" class="ds-icon__text--desktop">{{ desktopText }}</span>
    </span>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

interface Props {
  icon?: string
  iconSrc?: string
  text: string
  desktopText?: string
  variant?: 'inline' | 'bullet'
  color?: string
  size?: string | number
  align?: 'start' | 'center'
}

const props = withDefaults(defineProps<Props>(), {
  icon: undefined,
  iconSrc: undefined,
  desktopText: undefined,
  variant: 'inline',
  color: 'currentColor',
  size: '20px',
  align: 'start',
})

const iconStyle = computed(() => ({
  color: props.color,
  fontSize:
    typeof props.size === 'number'
      ? `${props.size}px`
      : props.size,
}))
</script>

<style scoped>
.ds-icon {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.ds-icon--bullet {
  align-items: center;
  gap: 8px;
}

.ds-icon--align-center { align-items: center; }

.ds-icon__circle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  min-width: 40px;
  border-radius: 50%;
  background-color: var(--ds-purple-main);
  color: #ffffff;
  font-size: 18px;
  flex-shrink: 0;
}

.ds-icon__circle--image { background-color: transparent; }

.ds-icon__image {
  width: 40px;
  height: 40px;
  display: block;
  object-fit: contain;
}

.ds-icon > i {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  width: 24px;
  min-width: 24px;
  margin-top: 2px;
  flex-shrink: 0;
}

.ds-icon__text {
  font-family: var(--ds-font-family-base);
  font-size: var(--ds-body3-size);
  font-weight: var(--ds-body3-weight);
  color: var(--ds-text-primary);
  line-height: 1.6;
}

.ds-icon__text--desktop { display: none; }

@media (min-width: 1025px) {
  .ds-icon__text--responsive { display: none; }
  .ds-icon__text--desktop { display: inline; }
}
</style>
